import { useEffect, useMemo, useState } from "react";
import { trpc } from "../../lib/trpc";
import { Icon } from "../ui/Icon";
import { SearchSelect, type SSOption } from "../ui/SearchSelect";
import { MCard, Chips, Sheet, MSelect } from "../mobile/primitives";
import "./StockOverviewSection.css";

/** Genel Bakış satırı — inventory.stockOverview çıktısıyla aynı şekil. */
export interface OverviewItem {
  kind: "stock" | "semi" | "finished";
  id: string;
  code: string;
  name: string;
  uom: string;
  reorderPoint: number | null;
  onHand: number;
  reserved: number;
  earmarked: number;
  available: number;
  lotCount: number;
  status: "tukendi" | "kritik" | "normal" | "esiksiz";
}

interface StockOverviewProps {
  onReceipt: (item: OverviewItem) => void;
  onAdjust: (item: OverviewItem) => void;
  onPoRequest: (item: OverviewItem) => void;
  onTrend: (item: OverviewItem) => void;
  isMobile?: boolean;
}

type OverviewStatus = OverviewItem["status"];
type Kind = OverviewItem["kind"];

// Tür segment kontrolü (tek seçim; "" = tümü) — hiç seçilmeme footgun'ı yok.
const KIND_SEGMENTS: [Kind | "", string][] = [["", "Tümü"], ["stock", "Ham Madde"], ["semi", "Yarı Mamül"], ["finished", "Mamul"]];
const KIND_LABEL_MAP: Record<string, string> = { stock: "Ham Madde", semi: "Yarı Mamül", finished: "Mamul" };
const STATUS_OPTIONS: [OverviewStatus | "", string][] = [["", "Tüm durumlar"], ["kritik", "Kritik"], ["tukendi", "Tükendi"], ["normal", "Normal"], ["esiksiz", "Eşik yok"]];
const KIND_BADGE: Record<Kind, { cls: string; label: string }> = {
  stock: { cls: "", label: "Ham Madde" },
  semi: { cls: "b-info", label: "Yarı Mamül" },
  finished: { cls: "b-success", label: "Mamul" },
};
const STATUS_META: Record<OverviewStatus, { cls: string; icon: string; label: string }> = {
  tukendi: { cls: "b-danger", icon: "x", label: "Tükendi" },
  kritik: { cls: "b-warn", icon: "alertTriangle", label: "Kritik" },
  normal: { cls: "b-success", icon: "check", label: "Normal" },
  esiksiz: { cls: "", icon: "boxes", label: "Eşik yok" },
};

function fmtQty(n: number): string {
  return new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 3 }).format(n);
}
function fmtMoney(n: number): string {
  return new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 0 }).format(n);
}
/** TR sayı biçimi: 12.500 → 12500 · 1.234,56 → 1234.56; geçersiz/boş → undefined (filtre uygulanmaz). */
function parseTrFilter(raw: string): number | undefined {
  const s = raw.trim();
  if (!s) return undefined;
  let n: number;
  if (/^\d{1,3}(\.\d{3})+(,\d+)?$/.test(s)) n = Number(s.replace(/\./g, "").replace(",", "."));
  else if (s.includes(",")) n = Number(s.replace(/\./g, "").replace(",", "."));
  else n = Number(s);
  return Number.isFinite(n) ? n : undefined;
}


/** Stok / Lotlar → Genel Bakış sekmesi: KPI şeridi + tüm türler için birleşik depo durumu tablosu. */
export function StockOverviewSection({ onReceipt, onAdjust, onPoRequest, onTrend, isMobile = false }: StockOverviewProps) {
  const [filterOpen, setFilterOpen] = useState(false);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<OverviewStatus | "">("");
  const [kind, setKind] = useState<Kind | "">(""); // tek tür seçimi ("" = tümü)
  const [itemTypeId, setItemTypeId] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [minQtyStr, setMinQtyStr] = useState("");
  const [maxQtyStr, setMaxQtyStr] = useState("");
  const pageSize = 25;

  // Kart türleri (Ayarlar → Kart Türleri) — ürün grubu filtresi; seçili türe göre daraltılır.
  const itemTypesQ = trpc.settings.itemType.list.useQuery({ includeInactive: false }, { staleTime: 60_000 });
  const typeOptions = useMemo<SSOption[]>(() =>
    ((itemTypesQ.data ?? []) as Array<{ id: string; kind: string; name: string }>)
      .filter((t) => !kind || t.kind === kind)
      .map((t) => ({ value: t.id, label: t.name, desc: KIND_LABEL_MAP[t.kind] ?? t.kind })),
    [itemTypesQ.data, kind]);
  // Tedarikçi filtresi (yalnız ham madde kartlarında varsayılan tedarikçi tanımlı)
  const suppliersQ = trpc.supplier.list.useQuery({ page: 1, pageSize: 100, isActive: true }, { staleTime: 60_000 });
  const supplierOptions = useMemo<SSOption[]>(() => (suppliersQ.data?.rows ?? []).map((s) => ({ value: s.id, label: s.name, tail: s.code })), [suppliersQ.data]);

  const input = useMemo(() => ({
    page, pageSize,
    q: q.trim() || undefined,
    kinds: kind ? [kind] as [Kind] : undefined,
    status: status || undefined,
    itemTypeId: itemTypeId || undefined,
    supplierId: supplierId || undefined,
    minQty: parseTrFilter(minQtyStr),
    maxQty: parseTrFilter(maxQtyStr),
  }), [page, q, status, kind, itemTypeId, supplierId, minQtyStr, maxQtyStr]);
  const ovQ = trpc.inventory.stockOverview.useQuery(input);

  const rows = (ovQ.data?.rows ?? []) as OverviewItem[];
  const total = ovQ.data?.total ?? 0;
  const kpis = ovQ.data?.kpis;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const anyFilter = !!(kind || status || itemTypeId || supplierId || minQtyStr || maxQtyStr);

  // Tür değişince o türe ait olmayan grup seçimini temizle (bağımlı filtre).
  function pickKind(k: Kind | "") { setKind(k); setItemTypeId(""); setPage(1); }
  function clearFilters() {
    setKind(""); setStatus(""); setItemTypeId(""); setSupplierId(""); setMinQtyStr(""); setMaxQtyStr(""); setPage(1);
  }

  // Veri küçülünce (ör. mal kabul sonrası kritik listesi kısalınca) OFFSET boşa düşerse ilk sayfaya dön.
  useEffect(() => {
    if (!ovQ.isFetching && page > 1 && rows.length === 0 && (kpis?.totalItems ?? 0) > 0) setPage(1);
  }, [ovQ.isFetching, page, rows.length, kpis?.totalItems]);

  // KPI kartı tıklanınca durum filtresi (tekrar tıkla = kaldır)
  function toggleStatus(s: OverviewStatus | "") {
    setStatus((cur) => (cur === s ? "" : s));
    setPage(1);
  }

  /**
   * STOK DEĞERİ — sunucu yalnız değeri BİLİNEN lotları toplar (`stock-value.ts tesisStokDegeri`; envanter raporu ve
   * Yönetici Dashboard'u ile aynı sayı). Maliyeti/₺ fiyatı olmayan lot 0 SAYILMAZ: o zaman değer bir ALT SINIRDIR →
   * "≥" öneki + "N lotun değeri bilinmiyor" notu (renk + ikon + metin; sessizlik yasağı).
   */
  const degerBilinmeyen = kpis?.stockValueUnknownLots ?? 0;
  const degerVal = kpis ? `${degerBilinmeyen > 0 ? "≥ " : ""}${fmtMoney(kpis.stockValue)}` : "…";
  const degerNot = degerBilinmeyen > 0 ? `${fmtQty(degerBilinmeyen)} lotun değeri bilinmiyor` : undefined;
  const degerTitle = degerBilinmeyen > 0
    ? `Değeri bilinen lotların ₺ toplamı. ${fmtQty(degerBilinmeyen)} lotun maliyeti/₺ fiyatı yok; değere 0 diye katılmadı — gerçek değer bundan büyüktür. Ayrıntı: Stok Raporu.`
    : "Değeri bilinen lotların ₺ toplamı (₺ olduğu kanıtlanan lot maliyeti; yoksa ham maddede alış fiyat listesi).";

  const kpiCard = (opts: { label: string; val: string; unit?: string; sub: string; note?: string; title?: string; color: string; active?: boolean; onClick?: () => void }) => (
    <button
      type="button"
      className="dash-kpi"
      style={{
        ["--kpi-color" as string]: opts.color,
        font: "inherit", textAlign: "left", cursor: opts.onClick ? "pointer" : "default", width: "100%",
        outline: opts.active ? "2px solid var(--primary)" : undefined, outlineOffset: opts.active ? 1 : undefined,
      }}
      onClick={opts.onClick}
      aria-pressed={opts.onClick ? !!opts.active : undefined}
      title={opts.title}
    >
      <div className="head"><span className="lbl">{opts.label}</span></div>
      <div className="val">{opts.val}{opts.unit ? <span className="unit"> {opts.unit}</span> : null}</div>
      <div className="sub">{opts.sub}</div>
      {opts.note ? <div className="ov-kpi-note"><Icon name="alertTriangle" size={11} />{opts.note}</div> : null}
    </button>
  );

  if (isMobile) {
    const filterCount = [itemTypeId, supplierId, minQtyStr, maxQtyStr].filter(Boolean).length;
    const kpiMob = (label: string, val: string, sub: string, color: string, active: boolean, onClick?: () => void, note?: string) => (
      <MCard pad onClick={onClick} className={active ? "sel" : ""} style={{ cursor: onClick ? "pointer" : "default" }}>
        <div className="m-ovl">{label}</div>
        <div className="mono" style={{ fontSize: 19, fontWeight: 700, color, marginTop: 2, lineHeight: 1.1 }}>{val}</div>
        <div style={{ fontSize: 10.5, color: "var(--neutral-500)", marginTop: 1 }}>{sub}</div>
        {note ? <div className="ov-kpi-note"><Icon name="alertTriangle" size={11} />{note}</div> : null}
      </MCard>
    );
    return (
      <>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {kpiMob("TOPLAM KALEM", kpis ? String(kpis.totalItems) : "…", "aktif kart", "var(--neutral-900)", status === "", () => toggleStatus(""))}
          {kpiMob("KRİTİK SEVİYE", kpis ? String(kpis.critical) : "…", "eşiğin altında", "var(--warning-text)", status === "kritik", () => toggleStatus("kritik"))}
          {kpiMob("TÜKENEN", kpis ? String(kpis.depleted) : "…", "stok sıfır", "var(--danger)", status === "tukendi", () => toggleStatus("tukendi"))}
          {kpiMob("STOK DEĞERİ ₺", degerVal, "değeri bilinen lotlar", "var(--success-text)", false, undefined, degerNot)}
        </div>
        <div className="m-search" style={{ height: 44 }}>
          <Icon name="search" size={15} />
          <input type="search" placeholder="Ürün kodu / adı ara…" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} aria-label="Ürün ara" />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Chips>
            {KIND_SEGMENTS.map(([val, lbl]) => (
              <button type="button" key={val || "all"} className={`m-chip ${kind === val ? "act" : ""}`} onClick={() => pickKind(val)}>{lbl}</button>
            ))}
          </Chips>
          <button type="button" className={`btn-md sm ${filterCount ? "primary" : "secondary"}`} style={{ flexShrink: 0 }} onClick={() => setFilterOpen(true)}><Icon name="list2" size={14} />{filterCount ? `(${filterCount})` : "Filtre"}</button>
        </div>

        {ovQ.isLoading ? <div className="m-empty">Yükleniyor…</div>
        : ovQ.isError ? <div className="m-empty" style={{ color: "var(--danger)" }}>{ovQ.error?.message}</div>
        : rows.length === 0 ? <div className="m-empty">{q ? `"${q}" için sonuç yok.` : anyFilter ? "Bu filtrelerle eşleşen kalem yok." : "Aktif ürün kartı yok."}</div>
        : rows.map((r) => {
          const sm = STATUS_META[r.status];
          const kb = KIND_BADGE[r.kind];
          return (
            <MCard pad key={`${r.kind}:${r.id}`}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: "block", fontSize: 13.5, fontWeight: 700 }}>{r.name}</span>
                  <span className="mono" style={{ display: "block", fontSize: 11, color: "var(--neutral-400)" }}>{r.code}</span>
                </span>
                <span className={`badge ${sm.cls === "b-warn" ? "b-warning" : sm.cls || "b-neutral"} no-dot`}><Icon name={sm.icon} size={11} />{sm.label}</span>
              </div>
              <div style={{ marginTop: 6 }}>
                <span className={`badge ${kb.cls || "b-neutral"} no-dot`}>{kb.label}</span>
                {r.lotCount ? <span className="mono" style={{ marginLeft: 8, fontSize: 11, color: "var(--neutral-500)" }}>{r.lotCount} lot</span> : null}
              </div>
              <div style={{ display: "flex", gap: 12, marginTop: 10 }}>
                {([["Eldeki", `${fmtQty(r.onHand)} ${r.uom}`, r.onHand <= 0 ? "var(--danger)" : "var(--neutral-900)"], ["Kullanılabilir", fmtQty(r.available), "var(--neutral-800)"], ["Eşik", r.reorderPoint != null ? fmtQty(r.reorderPoint) : "—", "var(--neutral-500)"]] as Array<[string, string, string]>).map(([l, v, c]) => (
                  <div key={l} style={{ flex: 1 }}>
                    <div className="m-ovl">{l}</div>
                    <div className="mono" style={{ fontSize: 12.5, fontWeight: 700, color: c, marginTop: 2 }}>{v}</div>
                  </div>
                ))}
              </div>
              {r.reserved > 0 ? <div className="mono" style={{ fontSize: 11, color: "var(--neutral-500)", marginTop: 4 }}>Rezerve: {fmtQty(r.reserved)} {r.uom}</div> : null}
              {/* Siparişe ayrılmış: rezervasyondan AYRI. Üretilen kalemde rezervasyon
                  yazılmadığı için bu miktar eskiden hiçbir yerde görünmüyordu. */}
              {r.earmarked > 0 ? <div className="mono" style={{ fontSize: 11, color: "var(--warning)", marginTop: 2 }}>Siparişe ayrılmış: {fmtQty(r.earmarked)} {r.uom}</div> : null}
              <div style={{ display: "flex", gap: 6, marginTop: 10, justifyContent: "flex-end" }}>
                <button type="button" className="m-iconbtn m-li-act" style={{ color: "var(--success)" }} aria-label="Mal kabul" onClick={() => onReceipt(r)}><Icon name="plus" size={16} /></button>
                <button type="button" className="m-iconbtn m-li-act" aria-label="Stok düzeltme" onClick={() => onAdjust(r)}><Icon name="edit" size={16} /></button>
                {r.kind === "stock" ? (
                  <button type="button" className="m-iconbtn m-li-act" style={{ color: "var(--warning)" }} aria-label="Satın alma talebi" onClick={() => onPoRequest(r)}><Icon name="cart" size={16} /></button>
                ) : null}
                <button type="button" className="m-iconbtn m-li-act" aria-label="Tüketim trendi" onClick={() => onTrend(r)}><Icon name="chart" size={16} /></button>
              </div>
            </MCard>
          );
        })}
        {totalPages > 1 ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, marginTop: 2 }}>
            <button type="button" className="btn-md secondary sm" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>‹ Önceki</button>
            <span className="mono" style={{ fontSize: 12.5, color: "var(--neutral-500)" }}>{page} / {totalPages}</span>
            <button type="button" className="btn-md secondary sm" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Sonraki ›</button>
          </div>
        ) : null}

        <Sheet open={filterOpen} title="Stok Filtresi" onClose={() => setFilterOpen(false)}
          footer={<>
            <button type="button" className="btn-md secondary" onClick={clearFilters}>Temizle</button>
            <button type="button" className="btn-md primary" onClick={() => setFilterOpen(false)}>Uygula</button>
          </>}>
          <div className="field">
            <label className="field-label">Ürün Grubu</label>
            <SearchSelect value={itemTypeId || null} options={typeOptions} onChange={(v) => { setItemTypeId(v ?? ""); setPage(1); }} placeholder="Tüm gruplar" searchPlaceholder="Grup ara…" emptyText={kind ? "Bu türde grup yok" : "Ayarlar → Kart Türleri"} />
          </div>
          <MSelect label="Durum" value={STATUS_OPTIONS.find(([v]) => v === status)?.[1] ?? "Tüm durumlar"} placeholder="Tüm durumlar"
            options={STATUS_OPTIONS.map(([, l]) => l)} onChange={(lbl) => { setStatus((STATUS_OPTIONS.find(([, l]) => l === lbl)?.[0] ?? "") as OverviewStatus | ""); setPage(1); }} />
          <div className="field">
            <label className="field-label">Tedarikçi <span style={{ color: "var(--neutral-400)", fontWeight: 500 }}>· ham madde</span></label>
            <SearchSelect value={supplierId || null} options={supplierOptions} onChange={(v) => { setSupplierId(v ?? ""); setPage(1); }} placeholder="Tüm tedarikçiler" searchPlaceholder="Tedarikçi ara…" />
          </div>
          <div className="field">
            <label className="field-label">Eldeki Aralık <span style={{ color: "var(--neutral-400)", fontWeight: 500 }}>· temel birim</span></label>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input className="m-input" type="text" inputMode="decimal" value={minQtyStr} placeholder="min" onChange={(e) => { setMinQtyStr(e.target.value.replace(/[^\d.,-]/g, "")); setPage(1); }} aria-label="alt sınır" />
              <span style={{ color: "var(--neutral-400)" }}>–</span>
              <input className="m-input" type="text" inputMode="decimal" value={maxQtyStr} placeholder="maks" onChange={(e) => { setMaxQtyStr(e.target.value.replace(/[^\d.,-]/g, "")); setPage(1); }} aria-label="üst sınır" />
            </div>
          </div>
        </Sheet>
      </>
    );
  }

  return (
    <>
      {/* KPI şeridi — tıklanınca tablo filtrelenir */}
      <div className="dash-kpi-grid" style={{ gridTemplateColumns: "repeat(4, 1fr)", marginBottom: 14 }}>
        {kpiCard({ label: "TOPLAM KALEM", val: kpis ? String(kpis.totalItems) : "…", sub: "aktif kart", color: "var(--primary)", active: status === "", onClick: () => toggleStatus("") })}
        {kpiCard({ label: "KRİTİK SEVİYE", val: kpis ? String(kpis.critical) : "…", sub: "eşiğin altında", color: "var(--warning)", active: status === "kritik", onClick: () => toggleStatus("kritik") })}
        {kpiCard({ label: "TÜKENEN", val: kpis ? String(kpis.depleted) : "…", sub: "stok sıfır", color: "var(--danger)", active: status === "tukendi", onClick: () => toggleStatus("tukendi") })}
        {kpiCard({ label: "STOK DEĞERİ", val: degerVal, unit: "₺", sub: "değeri bilinen lotlar", note: degerNot, title: degerTitle, color: "var(--success)" })}
      </div>

      <div className="dt2-wrap">
        <div className="dt2-toolbar ov-toolbar">
          <div className="dt2-search-box">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" />
            </svg>
            <input type="search" placeholder="Ürün kodu / adı ara…" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} aria-label="Ürün ara" />
          </div>
          {/* Tür segment kontrolü — tek seçim, "Tümü" varsayılan (hiç seçilmeme footgun'ı yok) */}
          <div className="form-radio-group" role="radiogroup" aria-label="Ürün türü" style={{ flexWrap: "nowrap" }}>
            {KIND_SEGMENTS.map(([val, lbl]) => (
              <label key={val || "all"} className="form-radio">
                <input type="radio" name="ov-kind" checked={kind === val} onChange={() => pickKind(val)} />
                <span>{lbl}</span>
              </label>
            ))}
          </div>
          {anyFilter ? (
            <button type="button" className="btn" onClick={clearFilters} title="Filtreleri temizle">
              <Icon name="x" size={13} />Temizle
            </button>
          ) : null}
          <button type="button" className="btn" onClick={() => ovQ.refetch()} disabled={ovQ.isFetching}>
            {ovQ.isFetching ? "Yükleniyor…" : "Yenile"}
          </button>
        </div>

        {/* Detaylı filtre çubuğu — ürün grubu + durum + tedarikçi + eldeki aralık (satıra yayılır) */}
        <div className="ov-filters">
          <div className="ov-filter">
            <span className="ov-filter-label">Ürün Grubu</span>
            <SearchSelect
              value={itemTypeId || null}
              options={typeOptions}
              onChange={(v) => { setItemTypeId(v ?? ""); setPage(1); }}
              placeholder="Tüm gruplar"
              searchPlaceholder="Grup ara…"
              emptyText={kind ? "Bu türde grup yok" : "Ayarlar → Kart Türleri'nden grup tanımlayın"}
            />
          </div>
          <div className="ov-filter">
            <span className="ov-filter-label">Durum</span>
            <select className="form-select" value={status} onChange={(e) => { setStatus(e.target.value as OverviewStatus | ""); setPage(1); }} aria-label="Durum filtresi">
              {STATUS_OPTIONS.map(([val, lbl]) => <option key={val || "all"} value={val}>{lbl}</option>)}
            </select>
          </div>
          <div className="ov-filter">
            <span className="ov-filter-label">Tedarikçi <span className="n">· ham madde</span></span>
            <SearchSelect
              value={supplierId || null}
              options={supplierOptions}
              onChange={(v) => { setSupplierId(v ?? ""); setPage(1); }}
              placeholder="Tüm tedarikçiler"
              searchPlaceholder="Tedarikçi ara…"
            />
          </div>
          <div className="ov-filter">
            <span className="ov-filter-label">Eldeki Aralık <span className="n">· temel birim</span></span>
            <div className="ov-range">
              <input className="form-input" type="text" inputMode="decimal" value={minQtyStr} placeholder="min"
                onChange={(e) => { setMinQtyStr(e.target.value.replace(/[^\d.,-]/g, "")); setPage(1); }} aria-label="Eldeki miktar alt sınır" />
              <span className="sep">–</span>
              <input className="form-input" type="text" inputMode="decimal" value={maxQtyStr} placeholder="maks"
                onChange={(e) => { setMaxQtyStr(e.target.value.replace(/[^\d.,-]/g, "")); setPage(1); }} aria-label="Eldeki miktar üst sınır" />
            </div>
          </div>
        </div>

        <div className="dt2-scroll">
          {ovQ.isLoading ? (
            <div className="empty-state"><p className="ttl">Yükleniyor…</p></div>
          ) : ovQ.isError ? (
            <div className="empty-state"><p className="ttl" style={{ color: "var(--danger)" }}>Veri yüklenemedi</p><p className="sub">{ovQ.error?.message}</p></div>
          ) : rows.length === 0 ? (
            <div className="empty-state">
              <p className="ttl">Kayıt yok</p>
              <p className="sub">{q ? `"${q}" için sonuç yok.` : anyFilter ? "Bu filtrelerle eşleşen kalem yok." : "Aktif ürün kartı yok."}</p>
            </div>
          ) : (
            <table className="dt2">
              <thead>
                <tr>
                  <th>Ürün</th>
                  <th style={{ width: 105 }}>Tür</th>
                  <th className="num" style={{ width: 120 }}>Eldeki</th>
                  <th className="num" style={{ width: 105 }}>Rezerve</th>
                  <th className="num" style={{ width: 115 }} title="Canlı bir müşteri siparişine ayrılmış stok — rezervasyondan ayrıdır">Siparişe Ayrılmış</th>
                  <th className="num" style={{ width: 120 }}>Kullanılabilir</th>
                  <th className="num" style={{ width: 100 }}>Eşik</th>
                  <th style={{ width: 110 }}>Durum</th>
                  <th className="num" style={{ width: 60 }}>Lot</th>
                  <th style={{ width: 120 }}>İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const sm = STATUS_META[r.status];
                  const kb = KIND_BADGE[r.kind];
                  return (
                    <tr key={`${r.kind}:${r.id}`}>
                      <td>
                        {r.name}
                        <span className="sec mono" style={{ fontSize: 10.5 }}>{r.code}</span>
                      </td>
                      <td><span className={`badge-pill ${kb.cls}`}>{kb.label}</span></td>
                      <td className="cell-num" style={{ color: r.onHand <= 0 ? "var(--danger)" : undefined }}>
                        {fmtQty(r.onHand)}<span className="unit">{r.uom}</span>
                      </td>
                      <td className="cell-num" style={{ color: "var(--neutral-500)" }}>{r.reserved > 0 ? fmtQty(r.reserved) : "—"}</td>
                      <td className="cell-num" style={{ color: r.earmarked > 0 ? "var(--warning)" : "var(--neutral-500)" }}
                        title={r.earmarked > 0 ? "Canlı bir müşteri siparişine ayrılmış (üretim partisi bağlı) — başka siparişe verilemez." : undefined}>
                        {r.earmarked > 0 ? fmtQty(r.earmarked) : "—"}
                      </td>
                      <td className="cell-num">{fmtQty(r.available)}<span className="unit">{r.uom}</span></td>
                      <td className="cell-num" style={{ color: "var(--neutral-500)" }}>{r.reorderPoint != null ? fmtQty(r.reorderPoint) : "—"}</td>
                      <td><span className={`badge-pill ${sm.cls}`}><Icon name={sm.icon} size={11} />{sm.label}</span></td>
                      <td className="cell-num">{r.lotCount || "—"}</td>
                      <td>
                        <div className="row-acts">
                          <button type="button" className="iact success" aria-label="Mal kabul" title="Mal kabul (stok girişi)" onClick={() => onReceipt(r)}>
                            <Icon name="plus" size={15} />
                          </button>
                          <button type="button" className="iact info" aria-label="Stok düzeltme" title="Stok düzeltme kaydı" onClick={() => onAdjust(r)}>
                            <Icon name="edit" size={15} />
                          </button>
                          {r.kind === "stock" ? (
                            <button type="button" className="iact warning" aria-label="Satın alma talebi" title="Satın alma talebi (taslak PO)" onClick={() => onPoRequest(r)}>
                              <Icon name="cart" size={15} />
                            </button>
                          ) : null}
                          <button type="button" className="iact info" aria-label="Tüketim trendi" title="Tüketim trendi (1/7/30/90 gün)" onClick={() => onTrend(r)}>
                            <Icon name="chart" size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="dt2-foot">
          <div><b>{total}</b> kalem · <b>{rows.length === 0 ? 0 : (page - 1) * pageSize + 1}–{(page - 1) * pageSize + rows.length}</b> arası</div>
          <div className="pager">
            <button type="button" className="pg" aria-label="Önceki sayfa" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>‹</button>
            <span className="pg active">{page}</span>
            <span style={{ color: "var(--neutral-400)", padding: "0 8px", fontSize: 12 }}>/ {totalPages}</span>
            <button type="button" className="pg" aria-label="Sonraki sayfa" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>›</button>
          </div>
        </div>
      </div>
    </>
  );
}
